import { ModalSystem } from './ModalSystem';
import { ToasterService } from './ToasterService';
import { oemConfig } from './OEMConfig';

/**
 * UserProfilePanel - Manages the user profile dropdown panel
 */
export class UserProfilePanel {
  private closeButton: HTMLElement | null = null;
  private signOutButton: HTMLElement | null = null;
  private viewProfileButton: HTMLElement | null = null;
  private settingsLink: HTMLElement | null = null;
  private teamLink: HTMLElement | null = null;
  private invitesLink: HTMLElement | null = null;
  private billingLink: HTMLElement | null = null;
  private supportLink: HTMLElement | null = null;

  /**
   * Initialize the user profile panel
   */
  public init(): void {
    this.closeButton = document.getElementById('user-dropdown-close');
    this.signOutButton = document.getElementById('user-signout');
    this.viewProfileButton = document.getElementById('profile-view-profile');
    this.settingsLink = document.getElementById('user-settings-link');
    this.teamLink = document.getElementById('user-team-link');
    this.invitesLink = document.getElementById('user-invites-link');
    this.billingLink = document.getElementById('user-billing-link');
    this.supportLink = document.getElementById('user-support-link');
    
    if (this.closeButton) {
      this.closeButton.addEventListener('click', () => this.close());
    }

    if (this.signOutButton) {
      this.signOutButton.addEventListener('click', () => this.handleSignOut());
    }

    if (this.viewProfileButton) {
      this.viewProfileButton.addEventListener('click', () => this.handleViewProfile());
    }

    if (this.settingsLink) {
      this.settingsLink.addEventListener('click', () => this.handleSettings());
    }

    if (this.teamLink) {
      this.teamLink.addEventListener('click', () => this.handleTeam());
    }

    if (this.invitesLink) {
      this.invitesLink.addEventListener('click', () => this.handleInvites());
    }

    if (this.billingLink) {
      this.billingLink.addEventListener('click', () => this.handleBilling());
    }

    if (this.supportLink) {
      this.supportLink.addEventListener('click', () => this.handleSupport());
    }

    // Update branding elements
    this.updateBranding();
  }

  /**
   * Update branding elements in the user panel
   */
  private updateBranding(): void {
    const branding = oemConfig.getBranding();
    
    // Update splash image link
    const splashLink = document.querySelector('.dropdown-content a[target="_blank"]') as HTMLAnchorElement;
    if (splashLink) {
      splashLink.href = branding.website;
      
      const splashImg = splashLink.querySelector('img') as HTMLImageElement;
      if (splashImg) {
        splashImg.src = branding.logo.splash;
        splashImg.alt = branding.caption;
      }
    }
  }

  /**
   * Close the dropdown panel
   */
  private close(): void {
    // Remove focus from dropdown to close it
    const activeElement = document.activeElement as HTMLElement;
    if (activeElement) {
      activeElement.blur();
    }
  }

  /**
   * Handle sign out click
   */
  private handleSignOut(): void {
    // Close the dropdown first
    this.close();

    // Show confirmation modal
    ModalSystem.questionYesNo(
      'Are you sure you want to sign out?',
      'Sign Out',
      () => {
        // User confirmed sign out - dispatch event for main.ts to handle
        window.dispatchEvent(new CustomEvent('flexiva:signout'));
      },
      undefined,
      'Yes',
      'No'
    );
  }

  /**
   * Handle view profile click - opens Entity form
   */
  private handleViewProfile(): void {
    // Close the dropdown first
    this.close();

    // Navigate to Entity form
    if (window.OpenForm) {
      window.OpenForm('entity');
    }
  }

  /**
   * Handle settings click - opens Settings form
   */
  private handleSettings(): void {
    // Close the dropdown first
    this.close();

    // Navigate to Settings form (use OEM-configured form or default)
    const settingsFormName = oemConfig.getFeatures().settingsForm || 'settings';
    if (window.OpenForm) {
      window.OpenForm(settingsFormName);
    }
  }

  /**
   * Handle team click - placeholder
   */
  private handleTeam(): void {
    this.close();
    ToasterService.info('Team management coming soon');
  }

  /**
   * Handle invites click - placeholder
   */
  private handleInvites(): void {
    this.close();
    ToasterService.info('Invites feature coming soon');
  }

  /**
   * Handle billing click - placeholder
   */
  private handleBilling(): void {
    this.close();
    ToasterService.info('Billing portal coming soon');
  }

  /**
   * Handle support click
   */
  private handleSupport(): void {
    this.close();
    window.open('https://support.trisys.co.uk', '_blank');
  }
}
